/**
 * Kit de marca de um espaço (Fase 3): cor, logótipo, letra e rodapé. Usado na página
 * pública, no embed e nas exportações. Sem kit, fica a marca Nolens Guides.
 */
import { PRODUCT } from './brand';

export type FontChoice = 'inter' | 'system' | 'serif' | 'mono' | 'custom';

export const FONT_CHOICES: FontChoice[] = ['inter', 'system', 'serif', 'mono', 'custom'];

export const FONT_STACKS: Record<FontChoice, string> = {
  inter: "Inter, -apple-system, 'Segoe UI', Roboto, sans-serif",
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'SF Mono', Menlo, Consolas, 'Courier New', monospace",
  custom: "'BrandFont', Inter, -apple-system, 'Segoe UI', Roboto, sans-serif",
};

export interface GuideBrand {
  /** Nome a mostrar ao lado do logótipo (por omissão, o do espaço). */
  name: string | null;
  accent: string;
  /** Endereço do logótipo (data: nas exportações, URL da API na web). */
  logo: string | null;
  font: FontChoice;
  /** Endereço do ficheiro WOFF2 quando font = custom. */
  fontUrl: string | null;
  /** Texto do rodapé (por omissão «Feito com Nolens Guides»). */
  footer: string;
  /** Formato editorial: cor da capa e de destaque (null = automáticas). */
  coverColor?: string | null;
  highlightColor?: string | null;
}

export const DEFAULT_BRAND: GuideBrand = {
  name: null,
  accent: PRODUCT.accent,
  logo: null,
  font: 'inter',
  fontUrl: null,
  footer: '',
};

export const isHexColor = (s: string) => /^#[0-9a-fA-F]{6}$/.test(s);

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Luminância relativa (WCAG). */
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** Cor do texto sobre a cor da marca (branco ou quase preto, o que se ler melhor). */
export const textOn = (hex: string) =>
  contrast(hex, '#ffffff') >= contrast(hex, '#141416') ? '#ffffff' : '#141416';

/** Versão clara da cor (fundo dos números dos passos). */
export function softOf(hex: string, amount = 0.88): string {
  const [r, g, b] = rgb(hex).map((v) => Math.round(v + (255 - v) * amount));
  return '#' + [r, g, b].map((v) => v!.toString(16).padStart(2, '0')).join('');
}

/**
 * A cor serve para texto (ligações, números) se tiver contraste ≥ 4,5 com o branco; senão
 * escurece-se até chegar lá, para que uma marca amarela continue legível.
 */
export function readableAccent(hex: string): string {
  let [r, g, b] = rgb(hex);
  for (
    let i = 0;
    i < 20 &&
    contrast('#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join(''), '#ffffff') < 4.5;
    i++
  ) {
    r = Math.round(r * 0.88);
    g = Math.round(g * 0.88);
    b = Math.round(b * 0.88);
  }
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}
